import { FacilityAlternateId } from "encamp-prisma/generated/browser";

type FindAltIdFields = Pick<FacilityAlternateId, "value" | "type"> & {
  // Changed this so it will work with the Front end
  hasNoId?: boolean;
  expiresAt?: Date | null;
};

function sortIds<T extends FindAltIdFields>(ids: T[]) {
  return [...ids].sort((a, b) => {
    if (a.expiresAt == null && b.expiresAt !== null) return -1;
    if (a.expiresAt != null && b.expiresAt === null) return 1;
    if (a.expiresAt && b.expiresAt) {
      return new Date(b.expiresAt).getTime() - new Date(a.expiresAt).getTime(); // Descending order
    }
    return 0;
  });
}

/**
 * Returns the first match of the provided `idTypes`.
 * @param ids - Collection of alternate IDs to search through
 * @param idTypes - Types of alternate IDs to search for
 * @param includeHasNoId - Whether to include alternate IDs with hasNoId set to true (default: `false`)
 * @returns The first match of the provided `idTypes` or undefined if no match is found
 * @example
 * ```ts
 * findAlternateId(facility.facilityAlternateIds, ["TIER2_MANAGER"])
 * ```
 */
export function findAlternateId<T extends FindAltIdFields>(
  ids: T[],
  idTypes: string[],
  includeHasNoId: boolean = false
): T | undefined {
  const sortedIds = sortIds<T>(ids);

  const match = sortedIds.find((x) => idTypes.includes(x.type) && (includeHasNoId || !x.hasNoId));

  return match;
}

/**
 * An alternate ID is "expired" once its `expiresAt` has passed. Expired IDs are
 * deliberately kept on the facility so history is preserved, but they should no
 * longer drive generator status: a deactivated site can have its EPA ID
 * reactivated and transferred to another company, and the stale association
 * would otherwise make the facility look like it is still generating.
 *
 * This is intentionally NOT applied inside `findAlternateId`/`findAlternateIds`
 * — those are used for every alternate-ID type across the app, and expiry only
 * matters for generator status. Callers in the generator-status paths filter
 * with this helper explicitly.
 */
export function isAlternateIdExpired(
  id: Pick<FindAltIdFields, "expiresAt">,
  asOf: Date = new Date()
): boolean {
  return id.expiresAt != null && new Date(id.expiresAt) <= asOf;
}

/**
 * Returns all matches of the provided `idTypes`.
 */
export function findAlternateIds<T extends FindAltIdFields>(
  ids: T[],
  idTypes: string[],
  includeHasNoId: boolean = false
): T[] {
  const sortedIds = sortIds<T>(ids);

  const matches = sortedIds.filter(
    (x) => idTypes.includes(x.type) && (includeHasNoId || !x.hasNoId)
  );

  return matches;
}
